import type { A2uiOperation } from "@lokasi-ai-web-sdk/agent-ui";

import { REPORT_ROOT_ID } from "./contract";

const SECTION_TYPE = "ReportSection";

type Component = Record<string, unknown>;

interface Folded {
  components: Map<string, Component>;
  data: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const pathKeys = (path: string) => path.split("/").filter(Boolean);

// ponytail: a path into an array item is not followed, the backend is expected
// to send a whole list at its path. A missed change only means a missing mark.
function setAt(data: unknown, keys: string[], value: unknown): unknown {
  if (keys.length === 0) return value;
  const [key, ...rest] = keys;
  const node = isRecord(data) ? data : {};
  return { ...node, [key]: setAt(node[key], rest, value) };
}

function getAt(data: unknown, path: string): unknown {
  return pathKeys(path).reduce<unknown>(
    (node, key) => (isRecord(node) ? node[key] : undefined),
    data
  );
}

/** Applies a version's operations in order, the way the surface model does. */
function fold(operations: readonly A2uiOperation[]): Folded {
  const folded: Folded = { components: new Map(), data: {} };

  for (const { updateComponents, updateDataModel } of operations) {
    if (
      isRecord(updateComponents) &&
      Array.isArray(updateComponents.components)
    ) {
      for (const component of updateComponents.components) {
        if (isRecord(component) && typeof component.id === "string") {
          folded.components.set(component.id, component);
        }
      }
    }
    if (isRecord(updateDataModel)) {
      folded.data = setAt(
        folded.data,
        pathKeys(String(updateDataModel.path ?? "/")),
        updateDataModel.value
      );
    }
  }
  return folded;
}

/**
 * What a block would draw: its own properties with every `{ path }` binding
 * swapped for the value it points at, keys sorted so their order never counts.
 */
function resolve(value: unknown, data: unknown): unknown {
  if (Array.isArray(value)) return value.map((item) => resolve(item, data));
  if (!isRecord(value)) return value;

  const keys = Object.keys(value).sort((a, b) => a.localeCompare(b));
  if (
    keys.length === 1 &&
    keys[0] === "path" &&
    typeof value.path === "string"
  ) {
    return getAt(data, value.path);
  }
  return Object.fromEntries(
    keys.map((key) => [key, resolve(value[key], data)])
  );
}

const signature = ({ components, data }: Folded, id: string) => {
  const component = components.get(id);
  return component === undefined
    ? undefined
    : JSON.stringify(resolve(component, data));
};

/** Ids a section lists; a templated list is drawn from its template. */
function childIdsOf(component: Component): string[] {
  const { children } = component;
  if (Array.isArray(children)) {
    return children.filter((id): id is string => typeof id === "string");
  }
  return isRecord(children) && typeof children.componentId === "string"
    ? [children.componentId]
    : [];
}

function reachableBlockIds({ components }: Folded): string[] {
  const blocks: string[] = [];
  const visited = new Set<string>();
  const queue = [REPORT_ROOT_ID];

  for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
    const component = components.get(id);
    if (!component || visited.has(id)) continue;
    visited.add(id);

    if (component.component === SECTION_TYPE) {
      queue.push(...childIdsOf(component));
    } else {
      blocks.push(id);
    }
  }
  return blocks;
}

/**
 * The blocks of `next` that read differently from `previous`, including ones
 * `previous` did not have. Both are a version's complete operations. Sections
 * only hold blocks, and a block nothing on screen reaches is not counted, so
 * every id returned is one the reader can find.
 */
export function changedBlockIds(
  previous: readonly A2uiOperation[],
  next: readonly A2uiOperation[]
): string[] {
  const before = fold(previous);
  const after = fold(next);

  return reachableBlockIds(after).filter(
    (id) => signature(after, id) !== signature(before, id)
  );
}
